// FINANCE-01/03 admin API client (ledger, cash book, COD remittances). Shapes mirror
// packages/domain/finance/src/http.ts. Money is integer paisa.
import { request } from "../api/client";

const s = (o: string, st: string) =>
  `/orgs/${encodeURIComponent(o)}/stores/${encodeURIComponent(st)}`;

export interface AccountBalance {
  id: string | null;
  code: string;
  name: string;
  kind: string;
  system: boolean;
  balance_paisa: number;
  debit_paisa: number;
  credit_paisa: number;
  active?: boolean;
  opening_paisa?: number;
  /** A supplier's contact details (custom accounts; migration 1027). */
  details?: AccountDetails;
  /** For If-Match on edits; 0 for a chart account not seeded yet. */
  version?: number;
}
export interface AccountDetails {
  phone?: string;
  pan?: string;
  address?: string;
  note?: string;
}
/** One line of an account's history (GET …/accounts/:id/entries). */
export interface AccountEntry {
  entry_id: string;
  occurred_at: string;
  memo: string;
  source_type: string;
  source_id: string;
  reversal: boolean;
  dimension: string | null;
  debit_paisa: number;
  credit_paisa: number;
  /** On the account's normal side: + raises the balance (money in, more owed). */
  change_paisa: number;
}
/** Every account in a period (GET …/finance/statement), amounts on the normal side. */
export interface StatementAccount {
  id: string | null;
  code: string;
  name: string;
  kind: string;
  system: boolean;
  active: boolean;
  opening_paisa: number;
  debit_paisa: number;
  credit_paisa: number;
  change_paisa: number;
  closing_paisa: number;
}
/** A journal line as posted: a custom account by ID, or a chart account by its code. */
export type PostLine = ({ account_id: string } | { account: string }) &
  ({ debit_paisa: number } | { credit_paisa: number });
export interface CashBook {
  from: string;
  to: string;
  accounts: {
    id: string;
    code: string;
    name: string;
    opening_paisa: number;
    in_paisa: number;
    out_paisa: number;
    closing_paisa: number;
  }[];
  entries: {
    entry_id: string;
    occurred_at: string;
    memo: string;
    account: string;
    reversal: boolean;
    in_paisa: number;
    out_paisa: number;
  }[];
}
export interface JournalEntry {
  id: string;
  occurred_at: string;
  memo: string;
  source_type: string;
  source_id?: string;
  created_by?: string | null;
  reversal_of: string | null;
  lines: {
    account: string;
    dimension?: string | null;
    debit_paisa: number;
    credit_paisa: number;
  }[];
}
export interface Remittance {
  id: string;
  courier_account_id: string;
  reference: string;
  received_at: string;
  amount_paisa: number;
  fees_paisa: number;
  state: "imported" | "matched" | "posted";
  lines?: RemittanceLine[];
}
export interface RemittanceLine {
  tracking_no: string;
  expected_paisa: number | null;
  collected_paisa: number;
  fee_paisa: number;
  return_fee_paisa: number;
  match: "matched" | "short" | "over" | "unknown" | "already_remitted" | null;
}
export interface CodSummaryRow {
  courier_account_id: string;
  remitted_paisa: number;
  collected_paisa: number;
  fees_paisa: number;
  short_paisa: number;
  short_parcels: number;
}

export const financeKeys = {
  all: (o: string, st: string) => ["finance", o, st] as const,
  accounts: (o: string, st: string) => ["finance", o, st, "accounts"] as const,
  cashBook: (o: string, st: string, from: string, to: string) =>
    ["finance", o, st, "cash-book", from, to] as const,
  journal: (o: string, st: string) => ["finance", o, st, "journal"] as const,
  entries: (o: string, st: string, id: string) => ["finance", o, st, "entries", id] as const,
  statement: (o: string, st: string, from: string, to: string) =>
    ["finance", o, st, "statement", from, to] as const,
  cod: (o: string, st: string) => ["finance", o, st, "cod"] as const,
};

export const financeApi = {
  accounts: (o: string, st: string) =>
    request<{ data: AccountBalance[] }>("GET", `${s(o, st)}/finance/accounts`),
  addAccount: (o: string, st: string, body: Record<string, unknown>, key: string) =>
    request<AccountBalance>("POST", `${s(o, st)}/finance/accounts`, { body, idempotencyKey: key }),
  cashBook: (o: string, st: string, from: string, to: string) =>
    request<CashBook>("GET", `${s(o, st)}/finance/cash-book`, { query: { from, to } }),
  journal: (o: string, st: string, cursor?: string) =>
    request<{ data: JournalEntry[]; next_cursor: string | null }>(
      "GET",
      `${s(o, st)}/finance/journal`,
      { query: { limit: "100", ...(cursor ? { cursor } : {}) } },
    ),
  updateAccount: (
    o: string,
    st: string,
    a: { id: string; version: number },
    body: { name?: string; details?: AccountDetails; active?: boolean },
  ) =>
    request<AccountBalance>("PATCH", `${s(o, st)}/finance/accounts/${encodeURIComponent(a.id)}`, {
      body,
      ifMatch: a.version,
    }),
  accountEntries: (o: string, st: string, id: string) =>
    request<{ data: AccountEntry[]; next_cursor: string | null }>(
      "GET",
      `${s(o, st)}/finance/accounts/${encodeURIComponent(id)}/entries`,
      { query: { limit: "100" } },
    ),
  /** `lifetime`: everything up to `to` (the statement takes any length; the cash book 400 days). */
  statement: (o: string, st: string, from: string, to: string, lifetime = false) =>
    request<{ from: string; to: string; accounts: StatementAccount[] }>(
      "GET",
      `${s(o, st)}/finance/statement`,
      { query: lifetime ? { lifetime: "1", to } : { from, to } },
    ),
  post: (o: string, st: string, body: Record<string, unknown>, key: string) =>
    request<{ id: string }>("POST", `${s(o, st)}/finance/journal`, { body, idempotencyKey: key }),
  reverse: (o: string, st: string, id: string, key: string) =>
    request<{ id: string }>(
      "POST",
      `${s(o, st)}/finance/journal/${encodeURIComponent(id)}/reverse`,
      {
        body: {},
        idempotencyKey: key,
      },
    ),
  codSummary: (o: string, st: string) =>
    request<{ data: CodSummaryRow[] }>("GET", `${s(o, st)}/finance/cod`),
  remittances: (o: string, st: string) =>
    request<{ data: Remittance[] }>("GET", `${s(o, st)}/finance/cod/remittances`),
  remittance: (o: string, st: string, id: string) =>
    request<Remittance>("GET", `${s(o, st)}/finance/cod/remittances/${encodeURIComponent(id)}`),
  addRemittance: (o: string, st: string, body: Record<string, unknown>, key: string) =>
    request<Remittance>("POST", `${s(o, st)}/finance/cod/remittances`, {
      body,
      idempotencyKey: key,
    }),
  matchRemittance: (o: string, st: string, id: string) =>
    request<Remittance>(
      "POST",
      `${s(o, st)}/finance/cod/remittances/${encodeURIComponent(id)}/match`,
      { body: {}, idempotent: true },
    ),
  postRemittance: (o: string, st: string, id: string) =>
    request<Remittance>(
      "POST",
      `${s(o, st)}/finance/cod/remittances/${encodeURIComponent(id)}/post`,
      { body: {}, idempotent: true },
    ),
  shortfalls: (o: string, st: string) =>
    request<{ data: (RemittanceLine & { reference: string })[] }>(
      "GET",
      `${s(o, st)}/finance/cod/shortfalls`,
    ),
};

/** Today in Nepal time (YYYY-MM-DD) and N days before it. */
export function nptDay(offsetDays = 0, now = Date.now()): string {
  return new Date(now + (5 * 60 + 45) * 60_000 - offsetDays * 86_400_000)
    .toISOString()
    .slice(0, 10);
}
